/**
 * usePaperId hook for paper ID input validation.
 *
 * Validates the paper ID format and checksum.
 * Paper ID format: 4 digits + 1 letter (checksum)
 * Checksum: number % 26 maps to letter (a=0, b=1, ..., z=25)
 */

import { useState, useCallback, useMemo } from 'react';

import { validatePaperId } from '@/types';

interface UsePaperIdReturn {
  value: string;
  paperId: number | null;
  isValid: boolean;
  error: string | null;
  onChange: (value: string) => void;
  reset: () => void;
}

/**
 * Hook for managing paper ID input with validation.
 */
export function usePaperId(initialValue = ''): UsePaperIdReturn {
  const [value, setValue] = useState(initialValue);

  const validation = useMemo(() => {
    if (!value) {
      return { valid: false, paperId: null, error: null };
    }

    const result = validatePaperId(value);
    if (!result.valid) {
      // Check if it's just incomplete (only digits so far)
      if (/^\d+$/.test(value)) {
        return { valid: false, paperId: null, error: null };
      }
      return {
        valid: false,
        paperId: null,
        error: 'Tarkistussumma ei täsmää (ID on numero + kirjain)',
      };
    }

    return { valid: true, paperId: result.paperId, error: null };
  }, [value]);

  const onChange = useCallback((newValue: string) => {
    // Only allow alphanumeric characters
    const cleaned = newValue.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
    setValue(cleaned);
  }, []);

  const reset = useCallback(() => {
    setValue('');
  }, []);

  return {
    value,
    paperId: validation.paperId,
    isValid: validation.valid,
    error: validation.error,
    onChange,
    reset,
  };
}

